import { FC, useMemo, useState } from 'react'
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { TabelaAcao } from '@/components/common/TabelaAcao'
import { CANAL_RECORTE_LABEL, CanalDeRecorte, formatBRL } from '@/components/dashboard/canais'
import { ConsultorComparativo } from '@/types/resumoAdmin'
import { CANAIS_COMERCIAIS, CANAL_LABEL } from '@/types/statusReserva'

/** Valor do filtro de canal para "Canal em branco" (reservas de mais de um canal ou sem carimbo). */
const CANAL_MISTO = 'MISTO'

type FiltroPendencias = '' | 'COM' | 'SEM'

/** Nome carimbado nas reservas; sem nome registrado, o começo do identificador. */
export const nomeDoConsultor = (c: ConsultorComparativo): string =>
  c.nome?.trim() || (c.consultorId ? `${c.consultorId.slice(0, 8)}…` : '—')

const semAcento = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()

interface Props {
  linhas: ConsultorComparativo[]
  /** Com canal no seletor global as pendências não são recortadas: a coluna e o filtro saem de cena. */
  recortadoPorCanal: boolean
  onAbrir: (c: ConsultorComparativo) => void
}

/**
 * Comparação entre consultores da Visão de Carteira, com filtros de coluna no mesmo
 * padrão da tela de Consultores (busca, seletores, "Limpar filtros"). O resumo já traz a
 * lista inteira do recorte, então o filtro é local — nenhuma chamada a mais.
 */
export const ComparacaoConsultores: FC<Props> = ({ linhas, recortadoPorCanal, onAbrir }) => {
  const [busca, setBusca] = useState('')
  const [canal, setCanal] = useState('')
  const [pendencias, setPendencias] = useState<FiltroPendencias>('')

  const filtroPendencias = recortadoPorCanal ? '' : pendencias
  const filtrando = !!(busca.trim() || canal || filtroPendencias)

  const filtradas = useMemo(() => {
    const termo = semAcento(busca.trim())
    return linhas.filter((c) => {
      if (termo && !semAcento(`${c.nome ?? ''} ${c.consultorId}`).includes(termo)) return false
      if (canal === CANAL_MISTO ? !!c.canal : canal && c.canal !== canal) return false
      if (filtroPendencias === 'COM' && !(c.pendencias > 0)) return false
      if (filtroPendencias === 'SEM' && c.pendencias > 0) return false
      return true
    })
  }, [linhas, busca, canal, filtroPendencias])

  const limparFiltros = () => {
    setBusca('')
    setCanal('')
    setPendencias('')
  }

  if (linhas.length === 0) {
    return <EstadoDeLista estado="vazio" mensagem="Nenhum consultor com reservas neste recorte" minHeight={120} />
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <TextField
          size="small"
          label="Buscar consultor"
          placeholder="Nome ou identificador"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          sx={{ minWidth: 260 }}
        />
        <TextField
          select
          size="small"
          label="Canal"
          value={canal}
          onChange={(e) => setCanal(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">Todos</MenuItem>
          {CANAIS_COMERCIAIS.map((c) => (
            <MenuItem key={c} value={c}>
              {CANAL_LABEL[c]}
            </MenuItem>
          ))}
          <MenuItem value={CANAL_MISTO}>Misto / sem carimbo</MenuItem>
        </TextField>
        {!recortadoPorCanal && (
          <TextField
            select
            size="small"
            label="Pendências"
            value={pendencias}
            onChange={(e) => setPendencias(e.target.value as FiltroPendencias)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="COM">Com pendências</MenuItem>
            <MenuItem value="SEM">Sem pendências</MenuItem>
          </TextField>
        )}
        {filtrando && (
          <>
            <Button onClick={limparFiltros} size="small">
              Limpar filtros
            </Button>
            <Typography variant="caption" color="text.secondary">
              {filtradas.length} de {linhas.length} consultores
            </Typography>
          </>
        )}
      </Stack>

      {filtradas.length === 0 ? (
        <EstadoDeLista
          estado="vazio"
          mensagem="Nenhum consultor com esses filtros"
          minHeight={120}
          acao={{ label: 'Limpar filtros', onClick: limparFiltros }}
        />
      ) : (
        <TabelaAcao<ConsultorComparativo>
          aria-label="Comparação entre consultores"
          colunas={[
            { titulo: 'Consultor', render: nomeDoConsultor },
            {
              titulo: 'Canal',
              render: (c) =>
                c.canal ? (CANAL_RECORTE_LABEL[c.canal as CanalDeRecorte] ?? c.canal) : 'Misto / sem carimbo',
            },
            { titulo: 'Volume', align: 'right', render: (c) => formatBRL(Number(c.volume ?? 0)) },
            {
              titulo: 'Conversão',
              align: 'right',
              render: (c) => (c.conversao != null ? `${String(c.conversao).replace('.', ',')}%` : '—'),
            },
            {
              titulo: recortadoPorCanal ? 'Pendências (não recortado por canal)' : 'Pendências',
              align: 'right',
              render: (c) => (recortadoPorCanal ? '—' : c.pendencias),
            },
          ]}
          linhas={filtradas}
          chaveDaLinha={(c) => c.consultorId}
          onAbrir={onAbrir}
        />
      )}
    </Box>
  )
}
